import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError, fmtDate, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { SignIn, SignOut, Printer, FilePdf, ScanSmiley, MapPin, HandPointing } from "@phosphor-icons/react";
import { printPayslip, monthLabel, downloadPayslipPdf } from "@/lib/payslip";
import { FaceCapture } from "@/components/hrms/FaceCapture";
import { getLocation, mapLink } from "@/lib/face";

const thisMonth = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }).slice(0, 7);
const tm = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—");

export const LEAVE_STATUS = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};
export const LeavePill = ({ s, testid }) => (
  <span className={`text-[10.5px] font-mono uppercase tracking-wider px-2 py-0.5 rounded ${LEAVE_STATUS[s] || ""}`} data-testid={testid}>{s}</span>
);
export const leaveTypeLabel = (t) => (t === "paid" ? "Paid Leave" : "Other");

export const PunchInfo = ({ info, testid }) => {
  if (!info) return null;
  const link = mapLink(info);
  return (
    <span className="inline-flex items-center gap-1 ml-1" data-testid={testid}>
      <span className={`text-[9.5px] font-mono uppercase px-1.5 py-0.5 rounded ${info.method === "face" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>{info.method === "face" ? "Face" : "Manual"}</span>
      {link && <a href={link} target="_blank" rel="noreferrer" className="text-primary" title={`±${info.accuracy || "?"} m`}><MapPin size={13} /></a>}
    </span>
  );
};

const MY_DAY = { full: ["Full Day", "bg-green-100 text-green-700"], half: ["Half Day", "bg-amber-100 text-amber-800"], absent: ["Absent", "bg-red-100 text-red-700"] };
const MyDay = ({ r, testid }) => {
  const [label, cls] = MY_DAY[r.day_type || "full"];
  return (
    <span className="inline-flex items-center gap-1" data-testid={testid}>
      <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${cls}`}>{label}</span>
      {r.late_minutes > 0 && <span className="text-[10.5px] text-red-600">Late {r.late_minutes} min</span>}
    </span>
  );
};

export const MyAttendance = ({ me, onChanged }) => {
  const [month, setMonth] = useState(thisMonth());
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cam, setCam] = useState(null);
  const load = useCallback(async () => {
    try { setRows((await api.get(`/hrms/attendance/mine?month=${month}`)).data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, [month]);
  useEffect(() => { load(); }, [load]);
  const punch = async (kind, extra) => {
    setBusy(true);
    try {
      const loc = await getLocation();
      if (loc.lat == null) {
        toast.error("Location is mandatory — turn on mobile location (GPS), allow location access for this site and try again");
        return;
      }
      await api.post(`/hrms/attendance/${kind}`, { ...extra, ...loc });
      toast.success(`${kind === "check-in" ? "Checked in" : "Checked out"}${extra.method === "face" ? " — face matched" : ""}`);
      setCam(null); onChanged(); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const onCapture = async (b) => {
    if (cam === "register") {
      try { await api.post("/hrms/face/register", b); toast.success("Face registered — you can now use Face attendance"); setCam(null); onChanged(); }
      catch (e) { toast.error(formatApiError(e)); }
      return;
    }
    await punch(cam, { method: "face", ...b });
  };
  const t = me.today_attendance;
  const canIn = !busy && !t;
  const canOut = !busy && !!t && !t.check_out;
  return (
    <div className="space-y-4">
      {!me.face && (
        <div className="card-flat p-4 flex items-center justify-between flex-wrap gap-3 border-amber-300 bg-amber-50" data-testid="face-register-card">
          <div className="text-[12.5px] text-amber-900">Register your face once to use Face attendance. Until then, use manual check-in.</div>
          <Button size="sm" onClick={() => setCam("register")} data-testid="face-register-self"><ScanSmiley size={15} className="mr-1.5" />Register My Face</Button>
        </div>
      )}
      <div className="card-flat p-5 flex items-center justify-between flex-wrap gap-4" data-testid="att-today">
        <div>
          <div className="eyebrow">Today · {fmtDate(me.today)}</div>
          <div className="text-sm mt-1" data-testid="att-today-status">
            {!t ? "Not checked in yet" : t.check_out ? `In ${tm(t.check_in)} · Out ${tm(t.check_out)} · ${t.hours} hrs` : `Checked in at ${tm(t.check_in)}`}
            {t && <PunchInfo info={t.in} testid="att-today-in-info" />}
            {t && <span className="ml-2"><MyDay r={t} testid="att-today-day" /></span>}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex gap-2 flex-wrap">
            <Button disabled={!canIn || !me.face} onClick={() => setCam("check-in")} data-testid="att-face-check-in"><ScanSmiley size={15} className="mr-1.5" />Face Check In</Button>
            <Button variant="outline" disabled={!canOut || !me.face} onClick={() => setCam("check-out")} data-testid="att-face-check-out"><ScanSmiley size={15} className="mr-1.5" />Face Check Out</Button>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="ghost" className="h-7 text-[11.5px]" disabled={!canIn} onClick={() => punch("check-in", { method: "manual" })} data-testid="att-check-in"><HandPointing size={13} className="mr-1" /><SignIn size={13} className="mr-1" />Manual Check In</Button>
            <Button size="sm" variant="ghost" className="h-7 text-[11.5px]" disabled={!canOut} onClick={() => punch("check-out", { method: "manual" })} data-testid="att-check-out"><HandPointing size={13} className="mr-1" /><SignOut size={13} className="mr-1" />Manual Check Out</Button>
          </div>
        </div>
      </div>
      <FaceCapture open={!!cam} title={cam === "register" ? "Register my face" : cam === "check-in" ? "Face Check In" : "Face Check Out"} onClose={() => setCam(null)} onCapture={onCapture} />
      <div className="flex items-center gap-3">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value || thisMonth())} className="w-44 h-9 font-mono" data-testid="att-month" />
        <span className="text-[12px] text-muted-foreground" data-testid="att-present-count">{rows.length} day(s) present in {monthLabel(month)}</span>
      </div>
      <div className="card-flat overflow-x-auto">
        <table className="data-table" data-testid="att-table">
          <thead><tr><th>Date</th><th>Check In</th><th>Check Out</th><th className="num">Hours</th><th>Day</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-muted-foreground">No attendance this month.</td></tr>}
            {rows.map((r) => <tr key={r.id}><td>{fmtDate(r.date)}</td><td>{tm(r.check_in)}<PunchInfo info={r.in} /></td><td>{tm(r.check_out)}<PunchInfo info={r.out} /></td><td className="num font-mono">{r.hours || "—"}</td><td><MyDay r={r} /></td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const MyLeave = () => {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ from_date: "", to_date: "", leave_type: "paid", reason: "" });
  const [saving, setSaving] = useState(false);
  const load = async () => { try { setRows((await api.get("/hrms/leaves/mine")).data); } catch (e) { toast.error(formatApiError(e)); } };
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!f.from_date || !f.to_date) { toast.error("Pick From and To dates"); return; }
    if (f.to_date < f.from_date) { toast.error("To date must be on or after From date"); return; }
    if (f.leave_type === "other" && !f.reason.trim()) { toast.error("Please specify the reason for Other leave"); return; }
    setSaving(true);
    try { await api.post("/hrms/leaves", f); toast.success("Leave request sent for approval"); setF({ from_date: "", to_date: "", leave_type: "paid", reason: "" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const cancel = async (id) => {
    if (!window.confirm("Cancel this leave request?")) return;
    try { await api.delete(`/hrms/leaves/${id}`); toast.success("Request cancelled"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="card-flat p-5 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="leave-form">
        <div className="space-y-1"><Label className="eyebrow">From *</Label><Input type="date" value={f.from_date} onChange={(e) => setF({ ...f, from_date: e.target.value })} data-testid="leave-from" /></div>
        <div className="space-y-1"><Label className="eyebrow">To *</Label><Input type="date" value={f.to_date} onChange={(e) => setF({ ...f, to_date: e.target.value })} data-testid="leave-to" /></div>
        <div className="space-y-1">
          <Label className="eyebrow">Leave Type *</Label>
          <Select value={f.leave_type} onValueChange={(v) => setF({ ...f, leave_type: v })}>
            <SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="paid">1. Paid Leave</SelectItem><SelectItem value="other">2. Other (specify reason)</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="space-y-1 md:col-span-4">
          <Label className="eyebrow">Reason {f.leave_type === "other" && "*"}</Label>
          <Textarea rows={2} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder={f.leave_type === "other" ? "Specify the reason" : "Optional"} data-testid="leave-reason" />
        </div>
        <div className="md:col-span-4 flex justify-end"><Button onClick={submit} disabled={saving} data-testid="leave-submit">Apply Leave</Button></div>
      </div>
      <div className="card-flat overflow-x-auto">
        <table className="data-table" data-testid="leave-mine-table">
          <thead><tr><th>Dates</th><th className="num">Days</th><th>Type</th><th>Reason</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">No leave requests yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`leave-mine-${r.id}`}>
                <td>{fmtDate(r.from_date)} → {fmtDate(r.to_date)}</td><td className="num font-mono">{r.days}</td>
                <td>{leaveTypeLabel(r.leave_type)}</td><td className="text-[12px]">{r.reason || "—"}</td>
                <td><LeavePill s={r.status} />{r.decision_note && <div className="text-[11px] text-muted-foreground">{r.decision_note}</div>}</td>
                <td className="text-right">{r.status === "pending" && <Button size="sm" variant="ghost" className="h-7 text-destructive" onClick={() => cancel(r.id)} data-testid={`leave-cancel-${r.id}`}>Cancel</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const MySlips = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { (async () => { try { setRows((await api.get("/hrms/salary-slips/mine")).data); } catch (e) { toast.error(formatApiError(e)); } })(); }, []);
  return (
    <div className="card-flat overflow-x-auto">
      <table className="data-table" data-testid="slips-table">
        <thead><tr><th>Month</th><th className="num">Worked Days</th><th className="num">Final Paid</th><th>Paid On</th><th></th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-muted-foreground">No salary slips yet — they appear here once salary is paid.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} data-testid={`slip-${r.id}`}>
              <td>{monthLabel(r.month)}</td><td className="num font-mono">{r.employee_working_days}</td>
              <td className="num font-mono">{inr(r.final_payable)}</td><td>{fmtDate(r.payment_date)}</td>
              <td className="text-right whitespace-nowrap">
                <Button size="sm" className="h-7 text-[11px] mr-1" onClick={() => downloadPayslipPdf(r.id)} data-testid={`slip-pdf-${r.id}`}><FilePdf size={13} className="mr-1" />Download PDF</Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => printPayslip(r.id)} data-testid={`slip-download-${r.id}`}><Printer size={13} className="mr-1" />Print</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
